import { useState } from "react";
import { Link } from "react-router";
import { Dialog, EmptyState, IconButton } from "@agentsaas/ui";
import type { Agent, Approval } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { useAgents, usePendingApprovals } from "../data/queries";
import { ApprovalCard } from "./ApprovalCard";

export function useApprovals(pid: string) {
  const approvals = usePendingApprovals(pid);
  const agents = useAgents(pid);
  const nameOf = (aid: string) => agents.data?.find((a: Agent) => a.id === aid)?.name ?? aid;
  return { items: approvals.data ?? [], loading: approvals.isPending, nameOf };
}

export function ApprovalList({ pid, items, nameOf }: { pid: string; items: Approval[]; nameOf(aid: string): string }) {
  const { t } = useT();
  if (!items.length) return <EmptyState icon="shield" title={t("approval.empty")} />;
  return (
    <div className="stack" data-testid="approval-list">
      {items.map((a) => (
        <div key={a.id} className="stack">
          <ApprovalCard approval={a} agentName={nameOf(a.agentId)} />
          <Link to={`/p/${pid}/a/${a.agentId}`} className="small" data-close-drawer>
            {t("approval.open_agent")}
          </Link>
        </div>
      ))}
    </div>
  );
}

/** 顶栏的待审批角标；点击打开列表（手机上 Dialog 自动变为底部 sheet） */
export function ApprovalBell({ pid }: { pid: string }) {
  const { t } = useT();
  const { items, nameOf } = useApprovals(pid);
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        icon="shield"
        label={t("approval.pending_count", { count: items.length })}
        badge={items.length}
        onClick={() => setOpen(true)}
        data-testid="approval-bell"
      />
      <Dialog open={open} onClose={() => setOpen(false)} title={t("approval.list_title")} closeLabel={t("shell.close")}>
        <div onClick={(e) => (e.target as HTMLElement).closest("a") && setOpen(false)}>
          <ApprovalList pid={pid} items={items} nameOf={nameOf} />
        </div>
      </Dialog>
    </>
  );
}
